import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";

interface Tab {
  id: number;
  label: string ;
}

interface SlidingTabsProps {
  tabs: Tab[];
  selectedTab: number;
  onTabChange: (id: number,label:string) => void;
  countArr?:Array<number>;
}

const SlidingTabs: React.FC<SlidingTabsProps> = ({
  tabs,
  selectedTab,
  onTabChange,
  countArr = null
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const labelRefs = useRef<(HTMLLabelElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  
  useEffect(() => {
    const index = tabs.findIndex((tab) => tab.id === selectedTab);

    // ❗ If invalid index → hide indicator
    if (index === -1) {
      setIndicatorStyle({ left: 0, width: 0 });
      return;
    }

    const label = labelRefs.current[index];
    if (label && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();

      setIndicatorStyle({
        left: labelRect.left - containerRect.left,
        width: labelRect.width,
      });
    }
  }, [selectedTab, tabs]);

  return (
    <StyledWrapper>
      <div className="tab-container overflow-x-auto whitespace-nowrap no-scrollbar" ref={containerRef}>
        {tabs.map((tab, index) => (
          <React.Fragment key={tab.id}>
            <input
              type="radio"
              name="tab"
              id={`tab${tab.id}`}
              className="tab"
              // Prevent checking when indicator is hidden
              checked={selectedTab === tab.id && indicatorStyle.width !== 0}
              onChange={() => onTabChange(tab.id,tab.label)}
            />
            <label
              ref={(el) => (labelRefs.current[index] = el)}
              className={`tab_label flex gap-1 !items-center ${
                selectedTab === tab.id ? "" : "hover:ring-[#007070] hover:ring-1  "
              } transition-colors duration-700 ease-in-out rounded-md`}
              
              htmlFor={`tab${tab.id}`}
            >
              {tab.label} {countArr && countArr[index]}
            </label>
          </React.Fragment>
        ))}
        <div
          className="indicator"
          style={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
          }}
        />
      </div>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  .tab-container {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 2px;
    background-color: #f5f5f5;
    border-radius: 9px;
  }

  .tab_label {
    padding: 0 16px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 400;
    position: relative;
    z-index: 2;
    transition: color 0.2s;
    white-space: nowrap;
    color: black; /* Default black */
  }

  .tab_label:hover {
    color: #007070;
  }

  .tab {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  /* Only highlight when radio is actually checked */
  .tab:checked + .tab_label {
    color: white;
    font-weight: 400;
  }

  .indicator {
    position: absolute;
    top: 2px;
    height: 30px;
    background-color: #007070;
    border-radius: 7px;
    z-index: 1;
    transition: all 0.2s ease-out;
    box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.12), 0px 3px 1px rgba(0, 0, 0, 0.04);
  }
`;

export default SlidingTabs;
